/*
  Newsletter-popup click pass-through (2026-08-26).

  THE PROBLEM. The newsletter popup is a full-screen overlay
  (#NewsletterPopup-<id>.modal, shown 5s after arrival via data-delay-seconds).
  While it is open it sits above everything, so the visitor's FIRST CLICK
  ANYWHERE on the site lands on the backdrop: the theme closes the popup and
  the click is consumed. Product tiles, nav, add-to-cart, the Kallare EVA chip
  -- all of them need a second click. Sitewide, ~80% of this shop's traffic is
  phone, so it is felt as "the site ignored me".

  THE FIX. A click on the BACKDROP still closes the popup exactly as before,
  and is then re-dispatched to whatever element was actually under the pointer.
  One tap, popup closes, the thing you aimed at responds.

  WHY IT IS BUILT THIS WAY:

  - Listens for `click` ONLY. On touch the browser synthesises exactly one
    click after touchend, so this fires once for mouse and once for touch --
    no touchend+click double-fire. Deliberately does NOT listen for touchend.

  - Re-dispatches a real MouseEvent rather than calling el.click(). el.click()
    works on anchors and buttons but silently does nothing useful for a lot of
    JS-bound widgets, and carries no coordinates. A dispatched, bubbling,
    cancelable MouseEvent triggers native link/button behaviour AND any
    addEventListener('click') handler, which is what the Kallare EVA chip is.

  - Uses document.elementFromPoint at the real pointer coordinates, AFTER
    forcing pointer-events:none on the overlay. The overlay may still be
    hit-testable for a frame while its close transition runs, and without that
    guard elementFromPoint would just return the overlay again.

  - Clicks INSIDE .modal__inner (email field, subscribe, close X) are left
    completely alone -- no interception, no pass-through.

  - The popup's own close path is untouched, so its cookie bookkeeping still
    runs and it does not re-open for the rest of the visit.
    data-delay-seconds is NOT touched; the 5s timing is Per's decision.
*/
(function () {
  'use strict';

  var SYNTHETIC = '__evaPopupPassThrough';
  var OPEN_CLASS = 'modal--is-active';
  var PANEL = '.modal__inner';

  function isOpenPopupOverlay(el) {
    return el
      && el.classList
      && el.classList.contains(OPEN_CLASS)
      && el.id
      && el.id.indexOf('NewsletterPopup-') === 0;
  }

  function forwardTo(overlay, x, y) {
    // The overlay can remain hit-testable for a frame while its close
    // transition runs; neutralise it before asking what is underneath.
    var previous = overlay.style.pointerEvents;
    overlay.style.pointerEvents = 'none';

    var target = document.elementFromPoint(x, y);

    if (!target || overlay.contains(target)) {
      overlay.style.pointerEvents = previous; // nothing to forward to -- put it back
      return;
    }

    var forwarded = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
      clientX: x,
      clientY: y,
      button: 0
    });
    forwarded[SYNTHETIC] = true;
    target.dispatchEvent(forwarded);

    // Always hand the overlay's pointer-events back. The popup is closed by
    // now so this is invisible either way, but leaving pointer-events:none
    // stuck on it would make the popup non-interactive if anything re-opened
    // it later in the visit (the theme has a 'reminder:openNewsletter' path).
    overlay.style.pointerEvents = previous;
  }

  // CAPTURE phase, deliberately. The theme's own close handler (Modals'
  // closeOffContentClick) runs in the bubble phase and removes
  // .modal--is-active synchronously -- so a bubble-phase listener here finds
  // the class already gone and cannot tell the click came from the overlay.
  // Capturing runs BEFORE that, while the overlay is still identifiable; the
  // actual forward is then deferred to after the close has happened.
  document.addEventListener('click', function (evt) {
    if (evt[SYNTHETIC]) { return; }              // never re-enter on our own event
    if (!evt.isTrusted) { return; }              // only real user input

    var overlay = evt.target && evt.target.closest
      ? evt.target.closest('.' + OPEN_CLASS)
      : null;
    if (!isOpenPopupOverlay(overlay)) { return; }

    // Inside the panel: normal behaviour, no interception at all.
    var panel = overlay.querySelector(PANEL);
    if (panel && panel.contains(evt.target)) { return; }

    // Backdrop click. Let the theme's own handler close the popup (and set its
    // cookie) on this same event, then forward the click on the next frame,
    // once the overlay is out of the way.
    var x = evt.clientX;
    var y = evt.clientY;
    if (typeof x !== 'number' || typeof y !== 'number') { return; }

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        forwardTo(overlay, x, y);
      });
    });
  }, true); // capture -- see the comment above this listener
})();
